import React, { useState, useEffect } from "react";

/**
 * UXHint — bloc de guidage contextuel discret et fermable
 *
 * Props :
 *   text     — contenu textuel (string ou JSX)
 *   variant  — "info" (bleu, défaut) | "tip" (jaune)
 *   id       — identifiant unique (string). Si fourni :
 *                • affiche une croix ✕ pour fermer
 *                • mémorise le choix dans localStorage ("uxhint_hide_<id>")
 *                • ne réaffiche pas le bandeau après rechargement
 *              Si omis : le bandeau est permanent (rétrocompatible).
 *   style    — styles supplémentaires sur le conteneur
 *
 * Bonus :
 *   clearAllUXHints() — utilitaire exporté pour réafficher tous les bandeaux
 *   clearUXHint(id)   — utilitaire exporté pour réafficher un bandeau précis
 */

const LS_PREFIX = "uxhint_hide_";

export const clearAllUXHints = () => {
  Object.keys(localStorage)
    .filter((k) => k.startsWith(LS_PREFIX))
    .forEach((k) => localStorage.removeItem(k));
};

export const clearUXHint = (id) => {
  if (id) localStorage.removeItem(`${LS_PREFIX}${id}`);
};

/* ── Styles ── */

const VARIANTS = {
  info: {
    background: "#F0F9FF",
    border: "1px solid #BAE6FD",
    color: "#0369A1",
  },
  tip: {
    background: "#FFFBEB",
    border: "1px solid #FDE68A",
    color: "#92400E",
  },
};

const ICON = { info: "ℹ️", tip: "💡" };

/* ── Composant ── */

const UXHint = ({ text, variant = "info", id, style }) => {
  // Si id fourni : lire localStorage pour savoir si fermé
  const [visible, setVisible] = useState(() => {
    if (!id) return true;
    return localStorage.getItem(`${LS_PREFIX}${id}`) !== "1";
  });

  // Sync si id change (changement de page dans une SPA)
  useEffect(() => {
    if (!id) return;
    setVisible(localStorage.getItem(`${LS_PREFIX}${id}`) !== "1");
  }, [id]);

  if (!visible) return null;

  const handleClose = () => {
    setVisible(false);
    if (id) localStorage.setItem(`${LS_PREFIX}${id}`, "1");
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "8px",
        padding: "10px 14px",
        borderRadius: "8px",
        fontSize: "0.78rem",
        lineHeight: 1.55,
        position: "relative",
        ...VARIANTS[variant],
        ...style,
        // paddingRight agrandi si bouton fermer présent
        paddingRight: id ? "36px" : "14px",
      }}
    >
      <span style={{ flexShrink: 0, marginTop: "1px" }}>{ICON[variant]}</span>
      <span style={{ flex: 1 }}>{text}</span>

      {id && (
        <button
          onClick={handleClose}
          title="Fermer ce conseil"
          style={{
            position: "absolute",
            top: "6px",
            right: "8px",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            fontSize: "0.75rem",
            lineHeight: 1,
            color: "inherit",
            opacity: 0.55,
            padding: "2px 4px",
            borderRadius: "4px",
            // Neutralise les overrides globaux de ui.css
            textTransform: "none",
            fontWeight: 400,
            transform: "none",
            transition: "opacity 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.55")}
        >
          ✕
        </button>
      )}
    </div>
  );
};

export default UXHint;
